import React, { useState, useEffect, useMemo, useRef } from "react";
import Map, {
  Marker,
  Popup,
  ScaleControl,
  FullscreenControl,
  NavigationControl,
  GeolocateControl,
  MapRef,
} from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
// import DataServices from "../../Services/DataServices";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import {
  Button,
  CardActionArea,
  CardActions,
  Grid2,
  ListItemText,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";

import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";

import BinIcon from "./BinIcon";
import Badge from "@mui/material/Badge";
import BinCategory from "../../../Utils/BinLegend";
import { LitterBin, Position } from "../../../Models/Models";
import { UrlConfigs } from "../../../Utils/CommonFunctions";
import Loading from "../../../Controller/LoadingController/Loading";
import supercluster from "supercluster";
import { GetMapLitterView } from "../../../ApiServices/ApiRequests";
import moment from "moment";
import { useTranslation } from "react-i18next";

const MAP_STYLE_URL = UrlConfigs.MAP_STYLE_URL; //'https://maps-{s}.onemap.sg/v3/Default/{z}/{x}/{y}.png'; //'mapbox://styles/mapbox/streets-v11';
const MAPBOX_ACCESS_TOKEN = UrlConfigs.MAPBOX_ACCESS_TOKEN;

const initialMapViewState = {
  latitude: 1.3179623906210907,
  longitude: 103.80952185215926,
  zoom: 12,
};

const bins = "BIN";
const projectOnly = "PROJECT";
const binTypeOnly = "BINTYPE";
const bothBinProject = "BOTH";

interface ViewBinData {
  binType: { label: string; value: string }[];
  project: { label: string; value: string }[];
  setProjectDetails: (
    e: {
      label: string;
      value: string;
      category: string;
      color: string;
      count: number;
    }[]
  ) => void;
}

const ViewBinInMap: React.FC<ViewBinData> = ({
  binType,
  project,
  setProjectDetails,
}) => {
  const { t } = useTranslation();
  const [load, setLoad] = useState(true);
  const [viewState, setViewState] = useState(initialMapViewState);
  const [map, setMap] = useState<mapboxgl.Map | null>(null);
  const [binData, setBinData] = useState<LitterBin[]>([]);
  const [duplicateList, setDuplicateList] = useState<LitterBin[]>([]);
  const [showPopup, setShowPopup] = useState(true);
  const [popUpDetails, setPopUpDetails] = useState<
    | supercluster.AnyProps
    | (supercluster.ClusterProperties & supercluster.AnyProps)
    | null
  >();

  const CountsRecords = useRef(0);

  const mapRef = useRef<MapRef | null>(null);
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      // Trigger actions when the container resizes
      if (mapRef.current) {
        mapRef.current.resize();
      }
    });

    const container = document.getElementById("ctmLitterBinmap-container");
    if (container) {
      resizeObserver.observe(container);
    }

    return () => {
      if (container) {
        resizeObserver.unobserve(container);
      }
    };
  }, []);

  useEffect(() => {
    getMapView();
  }, []);

  useEffect(() => {
    if (duplicateList.length > 0) {
      if (project.length == 0 && binType.length == 0) {
        setBinData(duplicateList);
      } else {
        let projectArray;
        let allowFilter: string =
          project.length > 0 && binType.length > 0
            ? bothBinProject
            : project.length > 0
            ? projectOnly
            : binTypeOnly;
        let projectText = project.map((x, i) => {
          return x.value;
        });
        let arr: LitterBin[] = [];
        if (allowFilter == bothBinProject) {
          projectArray = duplicateList.filter((item) =>
            projectText.includes(item?.projectType ?? "")
          );
          projectArray.length > 0 &&
            projectArray.map((x, i) => {
              binType.map((y, index) => {
                if (
                  y.value.replace(/\s/g, "") == x?.binType?.replace(/\s/g, "")
                ) {
                  arr.push(x);
                }
              });
            });
        } else if (allowFilter == projectOnly) {
          arr = duplicateList.filter((item) =>
            projectText.includes(item?.projectType ?? "")
          );
        } else if (allowFilter == binTypeOnly) {
          duplicateList.map((x, i) => {
            binType.map((y, index) => {
              if (x.binType) {
                if (
                  y.value.replace(/\s/g, "") == x?.binType?.replace(/\s/g, "")
                ) {
                  arr.push(x);
                }
              }
            });
          });
        } else {
          arr = duplicateList;
        }
        GetCounts(arr);
        setBinData(arr);
      }
    }
  }, [binType, project]);

  useEffect(() => {
    if (popUpDetails) {
      setShowPopup(true);
    }
  }, [popUpDetails]);

  useEffect(() => {
    if (!showPopup) {
      setPopUpDetails(null);
    }
  }, [showPopup]);

  const getMapView = async (): Promise<void> => {
    try {
      const data: LitterBin[] = await GetMapLitterView(bins);

      if (CountsRecords.current == 0) {
        GetCounts(data);
      }
      setBinData(data);
      setDuplicateList(data);
    } catch (err) {
      console.log("Error", err);
    }
    setLoad(false);
  };

  const GetCounts = async (data: LitterBin[]): Promise<void> => {
    var dummyData: LitterBin[] = [...data];
    var countingData: {
      label: string;
      value: string;
      category: string;
      color: string;
      count: number;
    }[] = BinCategory;
    countingData.map((x) => {
      x.count = 0;
    });
    if (binType.length > 0) {
      dummyData?.map((value, index) => {
        countingData.map((item) => {
          project.map((projects) => {
            if (projects.label == value.projectType) {
              if (
                item?.value?.replace(/\s/g, "") ==
                value?.binType?.replace(/\s/g, "")
              ) {
                item.count = item.count + 1;
              }
            }
          });
        });
      });
    }

    CountsRecords.current = 1;
    setProjectDetails(countingData);
  };

  const viewPopUp = (): JSX.Element | undefined => {
    if (popUpDetails) {
      return (
        <Popup
          longitude={parseFloat(popUpDetails.longitude)}
          latitude={parseFloat(popUpDetails.latitude)}
          anchor="center"
          closeOnClick={false}
          maxWidth={`320px`}
          onClose={() => setShowPopup(false)}
        >
          <Card sx={{ width: `320px`, height: `350px` }}>
            <List
              sx={{
                overflowY: "auto",
                margin: 0,
                padding: 0,
                listStyle: "none",
                height: "100%",

                "&::-webkit-scrollbar": {
                  width: "0.4em",
                },
                "&::-webkit-scrollbar-track": {
                  boxShadow: "inset 0 0 6px rgba(0,0,0,0.00)",
                  webkitBoxShadow: "inset 0 0 6px rgba(0,0,0,0.00)",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,.1)",
                  outline: "1px solid slategrey",
                },
              }}
            >
              <ListItem
                style={{ paddingLeft: `10px`, margin: `-12px 0px` }}
                disableGutters
              >
                <Grid2 container spacing={1}>
                  {popUpDetails.image && (
                    <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
                      <img
                        src={`${popUpDetails.image}`}
                        width={`100%`}
                        height={`250px`}
                      />
                    </Grid2>
                  )}
                </Grid2>
              </ListItem>

              {popUpDetails.deviceId != null && (
                <Grid2 container spacing={1} sx={{ marginY: 2 }}>
                  <Grid2 size={{ xs: 4, sm: 4, md: 4 }}>
                    <Typography
                      variant="body2"
                      style={{
                        whiteSpace: "pre-wrap",
                        overflowWrap: "break-word",
                        fontSize: `12px`,
                      }}
                    >
                      {t("global.deviceId")} :
                    </Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 8, sm: 8, md: 8 }}>
                    <Typography variant="body2" style={{ fontSize: `12px` }}>
                      {popUpDetails.deviceId}
                    </Typography>
                  </Grid2>
                </Grid2>
              )}

              {popUpDetails.projectType != null && (
                <Grid2 container spacing={1} sx={{ marginY: "5px" }}>
                  <Grid2 size={{ xs: 4, sm: 4, md: 4 }}>
                    <Typography
                      variant="body2"
                      style={{
                        whiteSpace: "pre-wrap",
                        overflowWrap: "break-word",
                        fontSize: `12px`,
                      }}
                    >
                      {t("global.ProjectType")}:
                    </Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 8, sm: 8, md: 8 }}>
                    <Typography variant="body2" style={{ fontSize: `12px` }}>
                      {popUpDetails.projectType}
                    </Typography>
                  </Grid2>
                </Grid2>
              )}

              {popUpDetails.binType != null && (
                <Grid2 container spacing={1} sx={{ marginY: "5px" }}>
                  <Grid2 size={{ xs: 4, sm: 4, md: 4 }}>
                    <Typography
                      variant="body2"
                      style={{
                        whiteSpace: "pre-wrap",
                        overflowWrap: "break-word",
                        fontSize: `12px`,
                      }}
                    >
                      {t("global.BinType")} :
                    </Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 8, sm: 8, md: 8 }}>
                    <Typography variant="body2" style={{ fontSize: `12px` }}>
                      {popUpDetails.binType}
                    </Typography>
                  </Grid2>
                </Grid2>
              )}

              {popUpDetails.address != null && (
                <Grid2 container spacing={1} sx={{ marginY: "5px" }}>
                  <Grid2 size={{ xs: 4, sm: 4, md: 4 }}>
                    <Typography
                      variant="body2"
                      style={{
                        whiteSpace: "pre-wrap",
                        overflowWrap: "break-word",
                        fontSize: `12px`,
                      }}
                    >
                      {t("global.address")} :
                    </Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 8, sm: 8, md: 8 }}>
                    <Typography variant="body2" style={{ fontSize: `12px` }}>
                      {popUpDetails.address}
                    </Typography>
                  </Grid2>
                </Grid2>
              )}
            </List>
          </Card>
        </Popup>
      );
    }
  };

  const getBinPopUp = (
    x:
      | supercluster.AnyProps
      | (supercluster.ClusterProperties & supercluster.AnyProps)
  ) => {
    setPopUpDetails(x);
  };

  const getIconSize = (zoom: number) => {
    let zoomData: number = zoom < 12 ? zoom : 12;
    return Math.pow(9 - zoomData, 2) * 3; // Adjust the size as needed
  };

  const SeverityBasedBin = (
    x:
      | supercluster.AnyProps
      | (supercluster.ClusterProperties & supercluster.AnyProps)
  ) => {
    //let imageData = x.level > 80 ? CriticalSvg : x.level > 50 ? ModerateSvg : CleanSvg
    return (
      <BinIcon
        width={getIconSize(viewState.zoom)}
        height={getIconSize(viewState.zoom)}
        fill={x.color ? x.color : "#000"}
      />
    );
  };

  {
    /* <img src={imageData} 
      style={{
      width: `${getIconSize(viewState.zoom)}px`,
      height: `${getIconSize(viewState.zoom)}px`,
      }}/>  CustomBinSvg*/
  }

  //once api is ready change to binData in 166line
  // const getBinDetails = () => {
  //   const clusters = createClusters(binData, viewState.zoom);

  //   return clusters.map((cluster, i) => {
  //     if (cluster.properties.cluster) {
  //       // Clustered marker
  //       return (
  //         <Marker
  //           key={`cluster-${i}`}
  //           latitude={cluster.geometry.coordinates[1]}
  //           longitude={cluster.geometry.coordinates[0]}
  //           onClick={() => getBinPopUp(cluster.properties)}
  //         >
  //           <div
  //             style={{
  //               width: 30,
  //               height: 30,
  //               backgroundColor: "red",
  //               borderRadius: "50%",
  //               display: "flex",
  //               justifyContent: "center",
  //               alignItems: "center",
  //               color: "white",
  //               fontSize: "12px",
  //              cursor:"not-allowed"
  //             }}

  //           >
  //             {cluster.properties.point_count}
  //           </div>
  //         </Marker>
  //       );
  //     } else {
  //       // Individual bin marker
  //       return (
  //         <Marker
  //           key={`bin-${cluster.properties.id}`}
  //           latitude={cluster.geometry.coordinates[1]}
  //           longitude={cluster.geometry.coordinates[0]}
  //           onClick={() => getBinPopUp(cluster.properties)}
  //         >
  //           {SeverityBasedBin({ ...cluster.properties, zoom: viewState.zoom })}
  //         </Marker>
  //       );
  //     }
  //   });
  // };

  const getBinDetails = () => {
    const clusters = createClusters(binData, viewState.zoom);

    return clusters.map((cluster, i) => {
      const coordinates = cluster.geometry?.coordinates;

      // Check if coordinates are valid
      if (
        !coordinates ||
        coordinates.length < 2 ||
        isNaN(coordinates[0]) ||
        isNaN(coordinates[1])
      ) {
        console.warn("Invalid coordinates for cluster or bin:", cluster);
        return null; // Skip rendering this marker
      }

      const [longitude, latitude] = coordinates;

      if (cluster.properties.cluster) {
        // Clustered marker
        return (
          <Marker
            key={`cluster-${i}`}
            latitude={latitude}
            longitude={longitude}
            onClick={() => console.warn("Click disabled for clustered markers")}
          >
            <div
              style={{
                width: 30,
                height: 30,
                backgroundColor: "red",
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                color: "white",
                fontSize: "12px",
              }}
            >
              {cluster.properties.point_count}
            </div>
          </Marker>
        );
      } else {
        // Individual bin marker
        return (
          <Marker
            key={`bin-${cluster.properties.id}`}
            latitude={latitude}
            longitude={longitude}
            onClick={() => getBinPopUp(cluster.properties)}
          >
            {SeverityBasedBin({ ...cluster.properties, zoom: viewState.zoom })}
          </Marker>
        );
      }
    });
  };

  const createClusters = (
    binData: any[],
    zoom: number
  ): (
    | supercluster.ClusterFeature<supercluster.AnyProps>
    | supercluster.PointFeature<supercluster.AnyProps>
  )[] => {
    const cluster: supercluster<supercluster.AnyProps, supercluster.AnyProps> =
      new supercluster({
        radius: 75, // Cluster radius (adjust as needed)
        maxZoom: 16, // Maximum zoom level (adjust as needed)
      });

    // Prepare markers for supercluster
    const markers: {
      type: string;
      geometry: {
        type: string;
        coordinates: any[];
      };
      properties: any;
    }[] = binData.map((bin) => ({
      type: "Feature", // Corrected to "Feature"
      geometry: {
        type: "Point", // Corrected to "Point"
        coordinates: [bin.longitude, bin.latitude],
      },
      properties: bin,
    }));
    cluster.load(markers);

    // Get clusters based on the current zoom level
    return cluster.getClusters([-180, -85, 180, 85], zoom);
  };

  const flyTo = (coordinates: any) => {
    map?.flyTo({
      center: [Number(coordinates.lngLat.lng), Number(coordinates.lngLat.lat)],
    });
  };

  return (
    <div
      id="ctmLitterBinmap-container"
      style={{ width: "100%", height: "75vh" }}
    >
      <Map
        ref={mapRef}
        initialViewState={viewState}
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        onMove={(evt) => setViewState(evt?.viewState)}
        style={{ width: `100%`, height: `100%` }}
        mapStyle={MAP_STYLE_URL}
        onLoad={(event) => setMap(event.target)}
        onClick={flyTo}
      >
        {getBinDetails()}
        {showPopup && viewPopUp()}
        <Loading loading={load} type={`glass`} backDrop={true} />
        <NavigationControl />
        <FullscreenControl />
        <GeolocateControl />
        <ScaleControl />
      </Map>
    </div>
  );
};

export default ViewBinInMap;
